import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Card, CardContent, Typography, Grid, Chip, Stack, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
  Drawer, Divider, CircularProgress, Alert,
} from '@mui/material';
import { Close, Receipt, AttachMoney, CheckCircle, OpenInNew } from '@mui/icons-material';
import { countyBillingService } from '../../services/county-billing.service';

const fmtKES = (v: string | number | null | undefined) =>
  `KES ${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const statusColor: Record<string, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'warning',
  paid_out: 'success',
  cancelled: 'error',
};

interface Props {
  /** When set, the panel filters drill-down by direction:
   * - "service_fee": county owes provider (pending_owe_provider)
   * - "revenue_share": provider owes county (pending_owed_to_me)
   * - undefined: show both
   */
  billingType?: 'service_fee' | 'revenue_share' | null;
}

export function CountySettlementsPanel({ billingType }: Props) {
  const [drillId, setDrillId] = useState<number | null>(null);

  const listQuery = useQuery({
    queryKey: ['county', 'settlements'],
    queryFn: () => countyBillingService.mySettlements(),
  });

  const data: any = (listQuery.data as any)?.data;
  const totals = data?.totals;
  const settlements = data?.settlements?.data ?? [];

  const drillQuery = useQuery({
    queryKey: ['county', 'settlement-transactions', drillId],
    queryFn: () => countyBillingService.mySettlementTransactions(drillId as number),
    enabled: !!drillId,
  });
  const drillData: any = (drillQuery.data as any)?.data;
  const drillTxns = drillData?.transactions?.data ?? [];
  const drillSettlement = drillData?.settlement;

  if (listQuery.isLoading) {
    return <Box display="flex" justifyContent="center" py={4}><CircularProgress size={28} /></Box>;
  }

  const showProviderCol = billingType !== 'revenue_share';
  const showCountyCol = billingType !== 'service_fee';

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>Settlements</Typography>

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {showProviderCol && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card variant="outlined" sx={{ borderLeft: (t) => `4px solid ${t.palette.warning.main}` }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      You owe the provider (pending)
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                      {fmtKES(totals?.pending_owe_provider)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Paid to date: {fmtKES(totals?.paid_to_provider)}
                    </Typography>
                  </Box>
                  <Receipt sx={{ fontSize: 32, color: 'warning.main' }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
        {showCountyCol && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <Card variant="outlined" sx={{ borderLeft: (t) => `4px solid ${t.palette.success.main}` }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Owed to you (pending)
                    </Typography>
                    <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
                      {fmtKES(totals?.pending_owed_to_me)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Disbursed to date: {fmtKES(totals?.paid_to_me)}
                    </Typography>
                  </Box>
                  <AttachMoney sx={{ fontSize: 32, color: 'success.main' }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Table */}
      {settlements.length === 0 ? (
        <Alert severity="info">No settlement batches yet. The system provider generates these periodically.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Period</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell align="right">Txns</TableCell>
                <TableCell align="right">Gross</TableCell>
                <TableCell align="right">Provider</TableCell>
                <TableCell align="right">County</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settlements.map((s: any) => (
                <TableRow key={s.id} hover>
                  <TableCell>
                    {new Date(s.period_start).toLocaleDateString()} → {new Date(s.period_end).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={(s.billing_type || '').replace('_', ' ')}
                      color={s.billing_type === 'service_fee' ? 'info' : 'success'}
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }} />
                  </TableCell>
                  <TableCell align="right">{s.transaction_count}</TableCell>
                  <TableCell align="right">{fmtKES(s.gross_collected)}</TableCell>
                  <TableCell align="right">{fmtKES(s.provider_share_total)}</TableCell>
                  <TableCell align="right">{fmtKES(s.county_share_total)}</TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={s.status} color={statusColor[s.status] || 'default'}
                      sx={{ fontWeight: 700, textTransform: 'capitalize' }} />
                    {s.status === 'paid_out' && s.payout_reference && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        ref: {s.payout_reference}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => setDrillId(s.id)}>
                      <OpenInNew fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Drill-down drawer */}
      <Drawer
        anchor="right"
        open={!!drillId}
        onClose={() => setDrillId(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 640 }, p: 3 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={700}>Settlement transactions</Typography>
          <IconButton onClick={() => setDrillId(null)}><Close /></IconButton>
        </Stack>
        <Divider sx={{ mb: 2 }} />

        {drillQuery.isLoading ? (
          <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
        ) : drillSettlement ? (
          <>
            <Stack spacing={1} sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="body2"><strong>Period:</strong> {new Date(drillSettlement.period_start).toLocaleDateString()} → {new Date(drillSettlement.period_end).toLocaleDateString()}</Typography>
              <Typography variant="body2"><strong>Transactions:</strong> {drillSettlement.transaction_count}</Typography>
              <Typography variant="body2"><strong>Gross:</strong> {fmtKES(drillSettlement.gross_collected)}</Typography>
              <Typography variant="body2"><strong>Provider total:</strong> {fmtKES(drillSettlement.provider_share_total)}</Typography>
              <Typography variant="body2"><strong>County total:</strong> {fmtKES(drillSettlement.county_share_total)}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2"><strong>Status:</strong></Typography>
                <Chip size="small" label={drillSettlement.status} color={statusColor[drillSettlement.status] || 'default'}
                  sx={{ fontWeight: 700, textTransform: 'capitalize' }} />
                {drillSettlement.status === 'paid_out' && (
                  <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                )}
              </Stack>
              {drillSettlement.payout_reference && (
                <Typography variant="body2"><strong>Payout reference:</strong> {drillSettlement.payout_reference}</Typography>
              )}
            </Stack>

            <Typography variant="overline" color="text.secondary">Transactions in this batch</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Ref</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Fee</TableCell>
                    <TableCell align="right">Provider</TableCell>
                    <TableCell align="right">County</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {drillTxns.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>No transactions.</TableCell></TableRow>
                  ) : drillTxns.map((t: any) => (
                    <TableRow key={t.id}>
                      <TableCell>{new Date(t.created_at).toLocaleDateString()}</TableCell>
                      <TableCell><Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{t.reference || '—'}</Typography></TableCell>
                      <TableCell align="right">{fmtKES(t.amount)}</TableCell>
                      <TableCell align="right">{t.convenience_fee ? fmtKES(t.convenience_fee) : '—'}</TableCell>
                      <TableCell align="right">{t.provider_share ? fmtKES(t.provider_share) : '—'}</TableCell>
                      <TableCell align="right">{t.county_share ? fmtKES(t.county_share) : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}
      </Drawer>
    </Box>
  );
}
